import type { ToolDefinition, ToolType } from "../types";

const FAVICON_GENERATOR_TOOL_TYPE = {
  label: "Free SEO Tool",
  icon: "Sparkles",
} as const satisfies ToolType;

const faviconGeneratorTool: ToolDefinition = {
  slug: "favicon-generator",
  name: "Free Favicon Generator Online | Convert PNG to ICO instantly",
  title: "Favicon Generator",
  description:
    "Create a professional favicon package from any image. Convert PNG or JPG to ICO, adjust padding and background, and download a ZIP with your site icons and HTML code.",
  longDescription:
    "Generate a complete, ready-to-deploy favicon package for your website in seconds. Simply upload your logo, select your preferred output format, adjust the padding and background settings, and download a single ZIP file containing your icons, web app manifest, and HTML code.",
  toolType: {
    label: "Site Icon Maker",
    icon: "AppWindow",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Includes HTML Snippet",
    "PWA Manifest Included",
    "Custom Padding Controls",
  ],
  category: "SEO Tools",
  categorySlug: "image-seo-tools",
  href: "/tools/favicon-generator",
  api: "/api/v1/favicon",
  keywords: [
    "snappy favicon generator app",
    "convert png to ico format",
    "site icon resolution inspector",
    "how to add favicon in html",
    "web app manifest maker",
    "standard website icon sizes guide",
    "change logo to browser tab icon",
    "website logo dimensions checker",
    "snappy free ico file creator",
    "turn picture into pwa graphic",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Create a Favicon for Your Website",
        description:
          "Follow these simple steps to convert your logo into a fully functional site icon package.",
      },
      benefits: {
        title: "Why Use Our Site Icon Generator?",
        description:
          "A well-formatted favicon improves user experience, strengthens brand recognition, and ensures your site looks professional in browser tabs and search results.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Everything you need to know about creating favicons, understanding formats, and adding them to your website.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Professional Favicon Generator for Website Branding",
        level: "h2",
        paragraphs: [
          "A favicon (short for favorite icon) is the small image that appears next to your website's title in a browser tab, bookmarks bar, and mobile home screens. If you want your site to look trustworthy, you need a high-quality site icon. Our free online favicon generator takes the guesswork out of resizing images and writing boilerplate code.",
          "Whether you need to convert a PNG to ICO for basic compatibility, or you want to generate modern WEBP site icons for a fast-loading Progressive Web App (PWA), this tool handles the entire workflow. You don't just get an image; you get a deployable package.",
        ],
        note: "A missing favicon can result in a generic browser icon, making your website harder to find when users have multiple tabs open.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "What's Included in Your Download",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Formatted Icon Files",
                icon: "Image",
                description:
                  "Your generated site icons in your chosen format (ICO, PNG, or WEBP) properly sized for web use.",
                accent: "primary",
              },
              {
                label: "Web App Manifest",
                icon: "FileJson",
                description:
                  "A site.webmanifest file used by Android and Chrome to install your website as an application.",
                accent: "primary",
              },
              {
                label: "HTML Implementation Code",
                icon: "Code",
                description:
                  "A text file containing the exact HTML <link> tags you need to copy and paste into your site's <head>.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        title: "Fine-Tune Your Site Icon Automatically",
        level: "h2",
        paragraphs: [
          "Creating a favicon isn't just about shrinking an image down to 32x32 pixels. Often, a logo looks cramped when forced into a tiny square. Our tool provides built-in padding and background controls so you can ensure your logo has enough breathing room.",
          "If your original image is a transparent PNG, you can choose to keep the background transparent, or fill it with a solid color to ensure it stands out perfectly whether your user is in light mode or dark mode.",
        ],
      },
      {
        kind: "code",
        title: "Example: How to Put a Favicon in HTML",
        code: '<head>\n  \n  <link rel="icon" type="image/x-icon" href="/favicon.ico">\n  \n  <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">\n  \n  <link rel="manifest" href="/site.webmanifest">\n</head>',
        language: "html",
        description:
          "When you use our tool, we generate a snippet just like this customized for your exact package.",
        note: "Always place your favicon link tags inside the <head> section of your HTML document.",
      },
    ],
    callouts: [
      {
        title: "Related SEO Image Tools",
        layout: "cards",
        iconColor: "accent",
        items: [
          {
            label: "SEO Image Optimizer",
            href: "/tools/optimize-seo-responsive-image",
            description:
              "Generate responsive WebP assets after your favicon package is ready.",
            accent: "accent",
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload the source image",
        description:
          "Add one image file (like your company logo) to the generator and confirm the preview looks correct.",
      },
      {
        number: "02",
        title: "Choose favicon settings",
        description:
          "Select your desired output extension (ICO, PNG, or WEBP), choose the background style, and adjust padding if the logo needs more breathing room.",
      },
      {
        number: "03",
        title: "Generate the favicon package",
        description:
          "Click 'Generate Favicon' to process the uploaded image with your current custom settings.",
      },
      {
        number: "04",
        title: "Download the result",
        description:
          "Save the generated ZIP package, extract the files to your website's directory, and copy the included HTML snippet into your site.",
      },
    ],
    benefits: [
      {
        icon: "Package",
        title: "Complete ZIP Package",
        description:
          "Don't settle for just an image. Download a full ZIP archive containing your icons, a site.webmanifest file, and the required HTML code.",
      },
      {
        icon: "Maximize",
        title: "Custom Padding Controls",
        description:
          "Prevent your logo from touching the edges of the browser tab. Add visual breathing room instantly using our padding slider.",
      },
      {
        icon: "Palette",
        title: "Background Adjustments",
        description:
          "Keep your image background transparent, or add a solid background color to ensure maximum visibility in dark mode.",
      },
      {
        icon: "FileCode",
        title: "Multiple Output Formats",
        description:
          "Choose between the classic, universally compatible .ico format or modern, lightweight .png and .webp variations.",
      },
    ],
    faqs: [
      {
        question:
          "How do I add a favicon to my website using the generated files?",
        answer:
          "Once you download and extract the ZIP package, upload the image files and the webmanifest file to your website's root directory. Then, open the included text file, copy the HTML snippet, and paste it inside the `<head>` section of your website's code.",
      },
      {
        question: "Do I still need a .ico file for my website in 2026?",
        answer:
          "Yes. While modern browsers perfectly support PNG and SVG favicons, providing a standard `favicon.ico` file is still highly recommended. It acts as a reliable fallback for older browsers, legacy RSS readers, and specific web crawlers that explicitly look for that file format.",
      },
      {
        question: "Why is my website icon blurry in Google Search results?",
        answer:
          "Blurry icons in Google Discover or search results usually occur if your source image is too small. Google recommends that your favicon be a multiple of 48px square (e.g., 48x48, 96x96, 144x144). Uploading a high-resolution source image to our generator prevents scaling blur.",
      },
      {
        question: "What is the difference between a favicon and a site icon?",
        answer:
          "They are technically the same thing. The term 'favicon' specifically refers to the classic `favicon.ico` file originally used in early web browsers. 'Site icon' or 'App icon' are modern terms that encompass the higher-resolution PNG and WEBP graphics used for mobile home screens and Progressive Web Apps (PWAs).",
      },
      {
        question: "Can I convert a JPG to ICO with a transparent background?",
        answer:
          "JPG files do not support transparency. If you upload a JPG, the background will remain solid in the generated favicon. To get a transparent favicon, you must upload an image format that supports transparency, like a PNG or WEBP file.",
      },
      {
        question:
          "How do I fix a favicon not showing in Google search results?",
        answer:
          "First, ensure your favicon is highly visible and follows sizing guidelines (a multiple of 48x48 pixels). Second, verify that the favicon link tag in your HTML is correct and that Googlebot is not blocked from crawling your image files. It may take a few days for Google to recrawl and update your site icon.",
      },
      {
        question: "What does the site.webmanifest file do?",
        answer:
          "The `site.webmanifest` file is a JSON document that provides information about a web application. It tells mobile browsers (especially Android/Chrome) how your website should behave when a user 'installs' it to their home screen, dictating the name, theme colors, and which icons to use.",
      },
    ],
    useCases: [
      {
        label: "Web Developers",
        icon: "Code",
        description:
          "Generate boilerplate HTML and manifest files instantly alongside optimized assets.",
        accent: "primary",
      },
      {
        label: "Business Owners",
        icon: "Briefcase",
        description:
          "Enhance brand trust by converting a company logo into a professional browser tab icon.",
        accent: "primary",
      },
      {
        label: "UI/UX Designers",
        icon: "PenTool",
        description:
          "Test how a logo reads at small scales by adjusting padding and background colors.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Website Creators",
        icon: "Globe",
        description:
          "Easily implement standard site icons without needing advanced image editing software.",
        accent: "accent",
      },
      {
        label: "SEO Specialists",
        icon: "Search",
        description:
          "Ensure sites follow technical best practices for search engine result page (SERP) visibility.",
        accent: "accent",
      },
    ],
  },
};

export default faviconGeneratorTool;
export { faviconGeneratorTool };
